import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { InputOption } from '@app/models/input-option';
import { SelectionsModel, ControlTypeEnum } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { asapScheduler, first, skip } from 'rxjs';

@Component({
  selector: 'app-metabolicSyndrome',
  templateUrl: './metabolicSyndrome.component.html',
  styleUrls: ['./metabolicSyndrome.component.css']
})
export class MetabolicSyndromeComponent implements OnInit {

  @Input() indication: IndicationDefinition;
  
  allIndications: IndicationGroup[] = [];
  
  numberOfSelectionsListItems: number[] = [1,2,3,4,5,6];
  numberOfSelectionsValue: number = 3;

  maleHdlUpperLimitVariableName = 'maleHdlUpperLimit';
  femaleHdlUpperLimitVariableName = 'femaleHdlUpperLimit';
  maleWaistLowerLimitVariableName = 'maleWaistLowerLimit';
  femaleWaistLowerLimitVariableName = 'femaleWaistLowerLimit';

  fpgUpperLimitVariableName = 'fpgUpperLimit';
  triglyceridesLimitVariableName = 'triglyceridesLimit';
  systolicBpLowerLimitVariableName = 'systolicBpLowerLimit';
  diastolicBpLowerLimitVariableName = 'diastolicBpLowerLimit';

  limitInputOptions: InputOption[][] = [
    [
      {name: this.maleHdlUpperLimitVariableName, displayName: 'Male HDL Upper Limit', order: 1, defaultValue: 40, maxlength: 5, measurement: 'mg/dL'},
      {name: this.femaleHdlUpperLimitVariableName, displayName: 'Female HDL Upper Limit', order: 2, defaultValue: 50, maxlength: 5, measurement: 'mg/dL'},
    ],
    [
      {name: this.maleWaistLowerLimitVariableName, displayName: 'Male Waist Lower Limit', order: 1, defaultValue: 101.6, maxlength: 5, measurement: 'cm'},
      {name: this.femaleWaistLowerLimitVariableName, displayName: 'Female Waist Lower Limit', order: 2, defaultValue: 88.9, maxlength: 5, measurement: 'cm'}
    ]
  ];

  singleInputOptions: InputOption[] = [
    {name: this.fpgUpperLimitVariableName, displayName: 'FPG Upper Limit', order: 1, defaultValue: 126, maxlength: 3, details: 'mg/dL'},
    {name: this.triglyceridesLimitVariableName, displayName: 'Triglycerides Lower Limit', order: 1, defaultValue: 150, maxlength: 5, details: 'mg/dL'},
    {name: this.systolicBpLowerLimitVariableName, displayName: 'Systolic BP Lower Limit', order: 1, defaultValue: 130, maxlength: 3, details: 'mm Hg'},
    {name: this.diastolicBpLowerLimitVariableName, displayName: 'Diastolic BP Lower Limit', order: 1, defaultValue: 85, maxlength: 3, measurement: 'mm Hg'}
  ]

  public formGroup: FormGroup = this.getDefaultFormGroup();

  constructor(
    private sharedService: SharedService,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    this.limitInputOptions[0].forEach(i => {
      i['customLabel'] = 'HDL Upper Limit (mg/dL) Upper Limit (<)';
    });

    this.limitInputOptions[1].forEach(i => {
      i['customLabel'] = 'Waist (cm) Lower Limit (>)';
    });

    this.singleInputOptions.find(i => i.name === this.fpgUpperLimitVariableName)['customLabel'] = 'FPG (mg/dL) Upper Limit (<)';
    this.singleInputOptions.find(i => i.name === this.triglyceridesLimitVariableName)['customLabel'] = 'Triglycerides (mg/dL) Lower Limit (>=)';
    this.singleInputOptions.find(i => i.name === this.systolicBpLowerLimitVariableName)['customLabel'] = 'Systolic BP (mm Hg) Lower Limit (>=)';
    this.singleInputOptions.find(i => i.name === this.diastolicBpLowerLimitVariableName)['customLabel'] = 'Diastolic BP (mm Hg) Lower Limit (>=)';

    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });

    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

   setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      asapScheduler.schedule(() => {
        if (this.indication.selections.some(i => i.controlName === 'criteria')){
          this.numberOfSelectionsValue = Number.parseInt(this.indication.selections.find(i => i.controlName === 'criteria').selectionValue);
        }
        
        // top single inputs
        if (this.indication.selections.some(i => i.controlName === this.fpgUpperLimitVariableName)){
          this.formGroup.controls.fpgLowerLimit.setValue(this.indication.selections.find(i => i.controlName === this.fpgUpperLimitVariableName).selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === this.triglyceridesLimitVariableName)){
          this.formGroup.controls.triglyceridesLimit.setValue(this.indication.selections.find(i => i.controlName === this.triglyceridesLimitVariableName).selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === this.systolicBpLowerLimitVariableName)){
          this.formGroup.controls.systolicBpLowerLimit.setValue(this.indication.selections.find(i => i.controlName === this.systolicBpLowerLimitVariableName).selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === this.diastolicBpLowerLimitVariableName)){
          this.formGroup.controls.diastolicBpLowerLimit.setValue(this.indication.selections.find(i => i.controlName === this.diastolicBpLowerLimitVariableName).selectionValue);
        }
        
        // bottom male/female inputs
        if (this.indication.selections.some(i => i.controlName === this.maleHdlUpperLimitVariableName)){
          this.formGroup.controls.maleHdlUpperLimit.setValue(this.indication.selections.find(i => i.controlName === this.maleHdlUpperLimitVariableName).selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === this.femaleHdlUpperLimitVariableName)){
          this.formGroup.controls.femaleHdlUpperLimit.setValue(this.indication.selections.find(i => i.controlName === this.femaleHdlUpperLimitVariableName).selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === this.maleWaistLowerLimitVariableName)){
          this.formGroup.controls.maleWaistLowerLimit.setValue(this.indication.selections.find(i => i.controlName === this.maleWaistLowerLimitVariableName).selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === this.femaleWaistLowerLimitVariableName)){
          this.formGroup.controls.femaleWaistLowerLimit.setValue(this.indication.selections.find(i => i.controlName === this.femaleWaistLowerLimitVariableName).selectionValue);
        }

        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  getDefaultFormGroup(): FormGroup {
    let form = {};
    this.limitInputOptions.forEach(set => {
      set.forEach(i => {
        form[i.name] = new FormControl(i.defaultValue, [
          Validators.required,
          Validators.minLength(1),
          Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal),
          Validators.min(0)
        ]);
      });
    });

    this.singleInputOptions.forEach(i => {
      form[i.name] = new FormControl(i.defaultValue, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal),
        Validators.min(0)
      ]);
    });

    return this.formBuilder.group(form);
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.numberOfSelectionsValue = 3;
      const flattenedInputOptions = this.limitInputOptions.flat();
      flattenedInputOptions.forEach(i => {
        this.formGroup.controls[i.name].setValue(i.defaultValue);
      });
      this.singleInputOptions.forEach(i => {
        this.formGroup.controls[i.name].setValue(i.defaultValue);
      });
      this.updateSelections();
    });
  }

  updateSelections(): void {
    const currentSelections: SelectionsModel[] = [];
    
    const criteriaSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: 'criteria',
      controlType: ControlTypeEnum.DROPDOWN,
      controlDisplayName: "Criteria",
      order: 1,
      selectionValue: this.numberOfSelectionsValue,
      selectionDisplayName: `Must satisfy ${this.numberOfSelectionsValue} of the following:`
    };
    currentSelections.push(criteriaSelection);

    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      const flattenedInputOptions = this.limitInputOptions.flat().concat(this.singleInputOptions);
      const option = flattenedInputOptions.find(i => i.name === key);
      const currentSelection: SelectionsModel = {
        indicationName: this.indication.name,
        controlName: key,
        controlType: ControlTypeEnum.TEXT,
        controlDisplayName: option.displayName,
        order: option.order,
        selectionValue: control.value,
        selectionDisplayName: option.displayName 
      };
      currentSelections.push(currentSelection);
    });

    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }

}
